<template>
    <div class="py-4 px-3" v-if="showingForm">
        <div id="authnet-form" class="mx-3" v-show="!reviewing">
            <div v-show="!addressVerified">
                <div
                    class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6 xl:px-60"
                >
                    <h3 class="sm:col-span-6">Shipping Address</h3>
                    <div
                        v-if="foundShippingAddress && suggestedShipping.errors"
                        class="sm:col-span-6 errors mb-3"
                        role="alert"
                    >
                        {{ suggestedShipping.errors }}
                    </div>
                    <div
                        v-if="foundBillingAddress && suggestedBilling.errors"
                        class="sm:col-span-6 errors mb-3"
                        role="alert"
                    >
                        {{ suggestedBilling.errors }}
                    </div>
                    <AddressInput
                        v-for="field in addressFields"
                        :key="field.id"
                        :field="field"
                        :model="shipping"
                    />
                    <div class="sm:col-span-6">
                        <span class="inline-flex items-center">
                            <input
                                id="isBillingDifferent"
                                type="checkbox"
                                class="form-checkbox h-3 w-3 text-blue-600 transition duration-150 ease-in-out mr-1"
                                v-model="isBillingDifferent"
                            />
                            <label
                                for="isBillingDifferent"
                                class="block text-sm font-medium leading-5 text-gray-700"
                            >
                                Billing Address different from Shipping Address
                            </label>
                        </span>
                    </div>
                </div>

                <div
                    v-if="isBillingDifferent"
                    class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6 xl:px-60"
                >
                    <h3 class="sm:col-span-6">Billing Address</h3>
                    <AddressInput
                        v-for="field in addressFields"
                        :key="field.id"
                        :field="field"
                        :model="billing"
                    />
                </div>
                <div class="mt-4 border-t border-gray-200 pt-4">
                    <div class="flex md:justify-end gap-3">
                        <button
                            type="button"
                            class="font-medium transition-all duration-300 border-2 rounded-md inline-block py-2 px-3 md:px-4 bg-leonard-transparent border-leonard-green text-leonard-green hover:bg-leonard-green hover:border-transparent hover:text-leonard-white focus:ring-leonard-green cursor-pointer"
                            @click="cancelPayment"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            class="font-medium transition-all duration-300 border-2 rounded-md inline-block py-2 px-3 md:px-4 bg-leonard-green border-leonard-green text-leonard-white hover:bg-leonard-green-hover hover:border-transparent focus:ring-leonard-green cursor-pointer disabled:bg-leonard-green-disabled disabled:text-leonard-green disabled:cursor-not-allowed"
                            :disabled="disableForms"
                            @click="CheckAddress"
                        >
                            Continue
                            <font-awesome-icon
                                v-if="disableForms"
                                icon="spinner"
                                spin
                            ></font-awesome-icon>
                        </button>
                    </div>
                </div>
            </div>
            <div v-show="addressVerified" class="mx-3">
                <div
                    class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6 xl:px-60"
                >
                    <h3
                        class="text-3xl font-medium text-gray-800 sm:col-span-6"
                    >
                        Billing Information
                    </h3>
                    <div class="sm:col-span-3">
                        <label for="card" class="text-sm">
                            Card Number
                        </label>
                        <div
                            class="w-full bg-leonard-white placeholder:text-leonard-black-disabled text-leonard-black rounded lg:rounded-md border border-leonard-grey focus:outline-none focus:border-leonard-black-hover"
                            :class="
                                payment.errors && payment.errors['card']
                                    ? 'not-valid'
                                    : ''
                            "
                        >
                            <cleave
                                id="card"
                                class="transition duration-150 ease-in-out w-full p-4"
                                :options="cleaveOptions"
                                @input="removeErrors(payment, 'card')"
                                v-model="payment.card"
                            />
                        </div>
                        <div
                            class="errors"
                            v-if="payment.errors && payment.errors['card']"
                        >
                            {{ payment.errors["card"] }}
                        </div>
                    </div>
                    <div class="sm:col-span-2">
                        <label for="expiry" class="text-sm">
                            Expiration Date
                        </label>
                        <div class="col-span-2">
                            <input
                                id="expiryMonth"
                                class="w-1/4 bg-leonard-white placeholder:text-leonard-black-disabled text-leonard-black rounded lg:rounded-md p-4 border border-leonard-grey focus:outline-none focus:border-leonard-black-hover"
                                :class="
                                    payment.errors &&
                                    (payment.errors['expiryYear'] ||
                                        payment.errors['expiryMonth'])
                                        ? 'not-valid'
                                        : ''
                                "
                                @input="removeErrors(payment, 'expiryMonth')"
                                v-model="payment.expiryMonth"
                                maxlength="2"
                                placeholder="01"
                            />
                            /
                            <input
                                id="expiryYear"
                                class="w-1/4 bg-leonard-white placeholder:text-leonard-black-disabled text-leonard-black rounded lg:rounded-md p-4 border border-leonard-grey focus:outline-none focus:border-leonard-black-hover"
                                :class="
                                    payment.errors &&
                                    (payment.errors['expiryYear'] ||
                                        payment.errors['expiryMonth'])
                                        ? 'not-valid'
                                        : ''
                                "
                                @input="removeErrors(payment, 'expiryYear')"
                                v-model="payment.expiryYear"
                                maxlength="2"
                                placeholder="35"
                            />
                        </div>
                        <div
                            class="errors"
                            v-if="
                                payment.errors &&
                                    (payment.errors['expiryMonth'] ||
                                        payment.errors['expiryYear'])
                            "
                        >
                            {{ payment.errors["expiryMonth"] }}
                        </div>
                    </div>
                    <div class="sm:col-span-1">
                        <label for="cvv" class="text-sm">
                            CVV
                            <font-awesome-icon fixed-width class="ml-1" :icon="['fas', 'credit-card']"></font-awesome-icon>
                        </label>
                        <input
                            id="cvv"
                            class="w-full bg-leonard-white placeholder:text-leonard-black-disabled text-leonard-black rounded lg:rounded-md p-4 border border-leonard-grey focus:outline-none focus:border-leonard-black-hover"
                            :class="
                                payment.errors && payment.errors['cvv']
                                    ? 'not-valid'
                                    : ''
                            "
                            @input="removeErrors(payment, 'cvv')"
                            v-model="payment.cvv"
                            maxlength="4"
                        />
                        <div
                            class="errors"
                            v-if="payment.errors && payment.errors['cvv']"
                        >
                            {{ payment.errors["cvv"] }}
                        </div>
                    </div>
                </div>
                <div class="mt-4 border-t border-gray-200 pt-4">
                    <div class="flex md:justify-end gap-3">
                        <button
                            type="button"
                            class="font-medium transition-all duration-300 border-2 rounded-md inline-block py-2 px-3 md:px-4 bg-leonard-transparent border-leonard-green text-leonard-green hover:bg-leonard-green hover:border-transparent hover:text-leonard-white focus:ring-leonard-green cursor-pointer"
                            @click="cancelPayment"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            class="font-medium transition-all duration-300 border-2 rounded-md inline-block py-2 px-3 md:px-4 bg-leonard-green border-leonard-green text-leonard-white hover:bg-leonard-green-hover hover:border-transparent focus:ring-leonard-green cursor-pointer disabled:bg-leonard-green-disabled disabled:text-leonard-green disabled:cursor-not-allowed"
                            :disabled="disableForms"
                            @click="proceedToReviewAuthnet"
                        >
                            Review
                            <font-awesome-icon
                                v-if="disableForms"
                                icon="spinner"
                                spin
                            ></font-awesome-icon>
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <div ref="authnet-review" class="mx-3" v-if="reviewing">
            <div class="grid grid-cols-12 gap-y-6 gap-x-4">
                <h3 class="text-3xl  col-span-12 font-medium text-gray-800">
                    Review Your Order
                </h3>
                <div class="col-span-12">
                    <div
                        class="flex item-wrapper mb-3"
                        :class="{
                            'border-t border-gray-50 pt-3':
                                i > 0 && !item.parent_id
                        }"
                        v-for="(item, i) in items"
                        :key="item.id"
                    >
                        <div class="flex-grow">
                            <div
                                class="flex flex-row leading-none mb-2"
                                v-if="!item.parent_id"
                            >
                                <div class="w-full text-gray-600 text-xs">
                                    {{ item.student.name }} -
                                    {{ item.access_code }}
                                </div>
                            </div>
                            <div
                                class="-mt-1 flex-grow"
                                :class="{
                                    'w-full': item.parent_id || item.is_rush
                                }"
                            >
                                {{ item.name }} - ${{ item.price | formatMoney
                                }}<br />
                                <span v-if="item.description">{{
                                    item.description
                                }}</span>
                                <ul
                                    class="flex flex-wrap flex-row mb-0"
                                    v-if="
                                        item.contents &&
                                            item.contents.length > 0
                                    "
                                >
                                    <li
                                        class="text-xs leading-loose text-blue-500"
                                    >
                                        Contents:
                                    </li>
                                    <li
                                        class="text-xs leading-loose ml-1"
                                        v-for="(name, i) in item.contents"
                                        :key="name"
                                    >
                                        {{ name }}
                                        <span v-if="i < item.contents.length - 1">,</span>
                                    </li>
                                </ul>
                                <ul
                                    class="flex flex-wrap flex-row mb-0"
                                    v-if="
                                        item.poses.length > 0 &&
                                            !item.parent_id
                                    "
                                >
                                    <li
                                        class="text-xs leading-loose text-blue-500"
                                    >
                                        Poses:
                                    </li>
                                    <li
                                        class="text-xs leading-loose ml-1"
                                        v-for="(pose, i) in item.poses"
                                        :key="pose"
                                    >
                                        {{ pose }}
                                        <span v-if="i < item.poses.length - 1">,</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    class="col-span-12 md:col-span-3 md:col-start-6 lg:col-start-8"
                >
                    <b> Ship<span v-if="!isBillingDifferent">/Bill</span> to </b
                    ><br />
                    {{ shipping.firstName }} {{ shipping.lastName }}<br />
                    {{ shipping.addressLine1 }}<br />
                    <span v-if="shipping.addressLine2">
                        {{ shipping.addressLine2 }}<br />
                    </span>
                    <span v-if="shipping.zipPlusFour">
                        {{ shipping.city }}, {{ shipping.state }}
                        {{ shipping.zipCode }} - {{ shipping.zipPlusFour }}<br />
                    </span>
                    <span v-else>
                        {{ shipping.city }}, {{ shipping.state }}
                        {{ shipping.zipCode }}<br />
                    </span>
                    {{ shipping.country }}
                </div>
                <div
                    v-if="isBillingDifferent"
                    class="col-span-12 md:col-span-3"
                >
                    <b>Bill to</b><br />
                    {{ billing.firstName }} {{ billing.lastName }}<br />
                    {{ billing.addressLine1 }}<br />
                    <span v-if="billing.addressLine2">
                        {{ billing.addressLine2 }}<br />
                    </span>
                    <span v-if="billing.zipPlusFour">
                        {{ billing.city }}, {{ billing.state }}
                        {{ billing.zipCode }} - {{ billing.zipPlusFour }}<br />
                    </span>
                    <span v-else>
                        {{ billing.city }}, {{ billing.state }} {{ billing.zipCode }}<br />
                    </span>
                    {{ billing.country }}
                </div>
                <div
                    class="col-span-12 md:col-span-6 md:col-start-6 lg:col-start-8"
                >
                    <table class="w-full" id="reviewTable">
                        <tr>
                            <td><b>Card:</b></td>
                            <td>{{ payment.card | formatCardNumber }}</td>
                        </tr>
                        <tr>
                            <td><b>Exp:</b></td>
                            <td>
                                {{ payment.expiryMonth }}/{{
                                    payment.expiryYear
                                }}
                            </td>
                        </tr>
                        <tr>
                            <td><b>Subtotal:</b></td>
                            <td>${{ payment.subtotal | formatMoney }}</td>
                        </tr>
                        <tr v-if="payment.discount > 0">
                            <td><b>Discount:</b></td>
                            <td class="text-green-600">
                                -${{ payment.discount | formatMoney }}
                            </td>
                        </tr>
                        <tr v-if="voucherAmt > 0">
                            <td><b>Voucher Applied:</b></td>
                            <td class="text-green-600">
                                -${{ voucherAmt | formatMoney }}
                            </td>
                        </tr>
                        <tr>
                            <td><b>Shipping & Handling:</b></td>
                            <td>
                                ${{ payment.shippingAndHandling | formatMoney }}
                            </td>
                        </tr>
                        <tr>
                            <td><b>Tax:</b></td>
                            <td>${{ payment.tax | formatMoney }}</td>
                        </tr>
                        <tr>
                            <td><b>Total:</b></td>
                            <td>${{ payment.totalFull | formatMoney }}</td>
                        </tr>
                    </table>
                </div>
            </div>
            <div class="mt-4 border-t border-gray-200 pt-4">
                <div class="md:text-right text-xs mb-4">
                    <h6>Terms &amp; Conditions:</h6>
                    <p>* Please allow 4-6 (2-3 with Rushed Processing) weeks for delivery of both print and digital packages.</p>
                    <p>** Prices and package contents are subject to change without notice.</p>
                </div>
                <div class="flex md:justify-end gap-3">
                    <button
                        type="button"
                        class="font-medium transition-all duration-300 border-2 rounded-md inline-block py-2 px-3 md:px-4 bg-leonard-transparent border-leonard-green text-leonard-green hover:bg-leonard-green hover:border-transparent hover:text-leonard-white focus:ring-leonard-green cursor-pointer"
                        @click="cancelPayment"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        class="font-medium transition-all duration-300 border-2 rounded-md inline-block py-2 px-3 md:px-4 bg-leonard-green border-leonard-green text-leonard-white hover:bg-leonard-green-hover hover:border-transparent focus:ring-leonard-green cursor-pointer disabled:bg-leonard-green-disabled disabled:text-leonard-green disabled:cursor-not-allowed"
                        @click="confirmAndPay"
                    >
                        Confirm and Pay
                        <font-awesome-icon
                            class="ml-1"
                            v-if="submitting"
                            icon="spinner"
                            spin
                        ></font-awesome-icon>
                    </button>
                </div>
            </div>
        </div>

        <!-- Modal Component -->
        <div
            v-if="!addressVerified"
            id="address-selection"
            data-backdrop="static"
            data-keyboard="false"
            class="modal fade"
            tabindex="-1"
            role="dialog"
            aria-hidden="true"
        >
            <div
                class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable"
                role="document"
            >
                <div class="modal-content">
                    <div class="modal-body">
                        <h3 class="text-center">Shipping Address</h3>
                        <p class="text-center">Please choose an address.</p>
                        <div
                            class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6 mt-5"
                        >
                            <div
                                data-address="provided"
                                class="sm:col-span-3"
                                @click="selectShippingAddress('provided')"
                            >
                                <div
                                    class="address-select border-dash"
                                    v-bind:class="{
                                        selected:
                                            shippingAddressSelected &&
                                            !useSuggestedShipping
                                    }"
                                >
                                    <h4>You Provided:</h4>
                                    <address>
                                        <h5>
                                            {{ shipping.firstName }}
                                            {{ shipping.lastName }}
                                        </h5>
                                        <p>{{ shipping.addressLine1 }}</p>
                                        <p v-if="shipping.addressLine2">
                                            {{ shipping.addressLine2 }}
                                        </p>
                                        <p>
                                            {{ shipping.city }},
                                            {{ shipping.state }}
                                            {{ shipping.zipCode }}
                                        </p>
                                        <p>{{ shipping.country }}</p>
                                    </address>
                                </div>
                            </div>

                            <div
                                v-if="this.shipping.country === 'United States'"
                                data-address="suggested"
                                class="sm:col-span-3"
                                @click="selectShippingAddress('suggested')"
                            >
                                <div
                                    class="address-select border-dash"
                                    v-bind:class="{
                                        selected:
                                            shippingAddressSelected &&
                                            useSuggestedShipping
                                    }"
                                >
                                    <h4>Suggested by USPS:</h4>
                                    <address>
                                        <h5>
                                            {{ suggestedShipping.firstName }}
                                            {{ suggestedShipping.lastName }}
                                        </h5>
                                        <p>
                                            {{ suggestedShipping.addressLine1 }}
                                        </p>
                                        <p v-if="suggestedShipping.addressLine2">
                                            {{ suggestedShipping.addressLine2 }}
                                        </p>
                                        <p v-if="suggestedShipping.zipPlusFour">
                                            {{ suggestedShipping.city }},
                                            {{ suggestedShipping.state }}
                                            {{ suggestedShipping.zipCode }} - {{ suggestedShipping.zipPlusFour }}
                                        </p>
                                        <p v-else>
                                            {{ suggestedShipping.city }},
                                            {{ suggestedShipping.state }}
                                            {{ suggestedShipping.zipCode }}
                                        </p>
                                        <p>{{ suggestedShipping.country }}</p>
                                    </address>
                                </div>
                            </div>
                        </div>

                        <div v-if="isBillingDifferent">
                            <h3 class="text-center mt-5">Billing Address</h3>
                            <p class="text-center">Please choose an address.</p>
                            <div
                                class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6 mt-5"
                            >
                                <div
                                    data-address="provided"
                                    class="sm:col-span-3"
                                    @click="selectBillingAddress('provided')"
                                >
                                    <div
                                        class="address-select border-dash"
                                        v-bind:class="{
                                            selected:
                                                shippingAddressSelected &&
                                                !useSuggestedBilling
                                        }"
                                    >
                                        <h4>You Provided:</h4>
                                        <address>
                                            <address>
                                                <h5>
                                                    {{ billing.firstName }}
                                                    {{ billing.lastName }}
                                                </h5>
                                                <p>
                                                    {{ billing.addressLine1 }}
                                                </p>
                                                <p v-if="billing.addressLine2">
                                                    {{ billing.addressLine2 }}
                                                </p>
                                                <p>
                                                    {{ billing.city }},
                                                    {{ billing.state }}
                                                    {{ billing.zipCode }}
                                                </p>
                                                <p>{{ billing.country }}</p>
                                            </address>
                                        </address>
                                    </div>
                                </div>
                                <div
                                    v-if="
                                        this.billing.country === 'United States'
                                    "
                                    data-address="suggested"
                                    class="sm:col-span-3"
                                    @click="selectBillingAddress('suggested')"
                                >
                                    <div
                                        class="address-select border-dash"
                                        v-bind:class="{
                                            selected:
                                                billingAddressSelected &&
                                                useSuggestedBilling
                                        }"
                                    >
                                        <h4>Suggested by USPS:</h4>
                                        <address>
                                            <h5>
                                                {{ suggestedBilling.firstName }}
                                                {{ suggestedBilling.lastName }}
                                            </h5>
                                            <p>
                                                {{
                                                    suggestedBilling.addressLine1
                                                }}
                                            </p>
                                            <p v-if="suggestedBilling.addressLine2">
                                                {{ suggestedBilling.addressLine2 }}
                                            </p>
                                            <p v-if="suggestedBilling.zipPlusFour">
                                                {{ suggestedBilling.city }},
                                                {{ suggestedBilling.state }}
                                                {{ suggestedBilling.zipCode }} - {{ suggestedBilling.zipPlusFour }}
                                            </p>
                                            <p v-else>
                                                {{ suggestedBilling.city }},
                                                {{ suggestedBilling.state }}
                                                {{ suggestedBilling.zipCode }}
                                            </p>
                                            <p>
                                                {{ suggestedBilling.country }}
                                            </p>
                                        </address>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer justify-center pt-0 border-t-0">
                        <button
                            type="button"
                            class="font-medium transition-all duration-300 border-2 rounded-md inline-block py-2 px-3 md:px-4 bg-leonard-transparent border-leonard-green text-leonard-green hover:bg-leonard-green hover:border-transparent hover:text-leonard-white focus:ring-leonard-green cursor-pointer"
                            data-dismiss="modal"
                            aria-label="Close"
                        >
                            Make Changes
                        </button>
                        <button
                            type="button"
                            class="font-medium transition-all duration-300 border-2 rounded-md inline-block py-2 px-3 md:px-4 bg-leonard-green border-leonard-green text-leonard-white hover:bg-leonard-green-hover hover:border-transparent focus:ring-leonard-green cursor-pointer disabled:bg-leonard-green-disabled disabled:text-leonard-green disabled:cursor-not-allowed"
                            @click="applySelectedAddresses"
                        >
                            Use Selected
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { library } from "@fortawesome/fontawesome-svg-core";
import { faSpinner, faCreditCard } from "@fortawesome/free-solid-svg-icons";
import luhn from "luhn";
import Cleave from "vue-cleave-component";
import Cart from "../../../classes/Cart";
import AddressVerificationService from "../../../classes/AddressVerificationService";
import AddressInput from "../../../components/AddressInput.vue";

export default {
    props: {
        items: Array,
        prefix: String,
        showingForm: Boolean,
        subtotal: Number,
        discount: [Number, String],
        shippingAndHandling: Number,
        voucher: String,
        voucherAmt: [Number, String],
        tax: Number,
        totalFull: Number
    },
    filters: {
        formatMoney(amount) {
            return parseFloat(amount)
                .toFixed(2)
                .replace(/\d(?=(\d{3})+\.)/g, "$&,");
        },
        formatCardNumber(number) {
            return `ending in ${number.slice(-4)}`;
        }
    },
    data() {
        return {
            Cart: new Cart(),

            reviewing: false,
            submitting: false,
            disableForms: false,

            addressApiService: new AddressVerificationService(),
            addressFields: this.getAddressFields(),
            addressVerified: false,

            foundShippingAddress: false,
            useSuggestedShipping: false,
            shippingAddressSelected: false,
            shipping: this.createAddress(),
            suggestedShipping: this.createAddress(),

            isBillingDifferent: false,
            foundBillingAddress: false,
            useSuggestedBilling: false,
            billingAddressSelected: false,
            billing: this.createAddress(),
            suggestedBilling: this.createAddress(),

            payment: this.createPayment(),
            requiredFields: {
                address: [
                    "firstName",
                    "lastName",
                    "country",
                    "addressLine1",
                    "city",
                    "state",
                    "zipCode"
                ],
                payment: ["card", "cvv", "expiryMonth", "expiryYear"]
            },
            cleaveOptions: { creditCard: true }
        };
    },
    components: { AddressInput, Cleave },
    created() {
        library.add(faSpinner, faCreditCard);
    },
    methods: {
        // payment methods
        createPayment() {
            return {
                card: null,
                cardType: null,
                expiryMonth: null,
                expiryYear: null,
                cvv: null,
                expiryStr: null,
                subtotal: null,
                discount: null,
                shippingAndHandling: null,
                tax: null,
                totalFull: null,
                errors: {}
            };
        },
        clearData() {
            this.shipping = this.createAddress();
            this.billing = this.createAddress();
            this.payment = this.createPayment();
            this.addressVerified = false;
            this.reviewing = false;
            this.submitting = false;
            this.useSuggestedShipping = false;
            this.useSuggestedBilling = false;
            this.isBillingDifferent = false;
        },
        cancelPayment() {
            this.clearData();
            this.$emit("cancelPayment");
        },
        buildItems(items) {
            return items.map(i => ({
                student_name: i.student.name,
                access_code: i.access_code,
                name: i.name,
                quantity: i.quantity,
                price: i.price,
                description: i.description,
                contents: i.contents,
                poses: i.poses
            }));
        },
        async confirmAndPay() {
            this.submitting = true;
            setTimeout(null, 500);
            const items = await this.buildItems(this.items);
            const data = {
                user_id: this.$auth.user().details.id,
                data: {
                    card: this.payment.card,
                    expiry: this.payment.expiryStr,
                    cvv: this.payment.cvv,
                    fullName:
                        this.shipping.firstName + " " + this.shipping.lastName,
                    firstName: this.shipping.firstName,
                    lastName: this.shipping.lastName,
                    shippingAddress: this.shipping,
                    billingAddress: this.billing,
                    total: this.totalFull,
                    discount: this.discount,
                    voucher: this.voucher,
                    voucherAmt: this.voucherAmt,
                    items: items,
                    tax: this.tax,
                    shippingAndHandling: this.shippingAndHandling
                }
            };
            this.Cart.submitAuthnet(data).then(response => {
                if (!response.success) {
                    this.cancelPayment();
                    feedback(response.message, "danger");
                    return;
                }
                const transaction = {
                    transId: response.data.transactionId,
                    orderInvoiceNumber: response.data.invoiceNumber,
                    billTo: this.billing,
                    shipTo: this.shipping
                };
                this.$emit("updateCartStatus", transaction);
            });
        },
        proceedToReviewAuthnet() {
            this.disableForms = true;

            const fieldNames = {
                card: "Credit card",
                expiryMonth: "Expiration date",
                expiryYear: "Expiration date",
                cvv: "Security code"
            };
            let errors = false;
            const setErrors = (model, fields) => {
                this.$set(model, "errors", {});
                fields.forEach(f => {
                    if (!model[f]) {
                        this.$set(
                            model.errors,
                            f,
                            `${fieldNames[f]} must be provided.`
                        );
                        errors = true;
                    }
                });
            };
            setErrors(this.payment, this.requiredFields.payment);
            if (!errors && !luhn.validate(this.payment.card)) {
                this.$set(this.payment.errors, "card", "Invalid credit card.");
                errors = true;
            }
            if (
                !errors &&
                (isNaN(parseInt(this.payment.expiryMonth)) ||
                    isNaN(parseInt(this.payment.expiryYear)) ||
                    this.payment.expiryYear.length > 2 ||
                    this.payment.expiryMonth.length > 2)
            ) {
                this.$set(this.payment.errors, "expiryMonth", "Example: 01/35");
                errors = true;
            }
            const lastDayOfMonth = new Date(
                `20${this.payment.expiryYear}`,
                this.payment.expiryMonth,
                0
            );
            if (!errors && !this.isFutureDate(lastDayOfMonth)) {
                this.$set(
                    this.payment.errors,
                    "expiryMonth",
                    "Please enter a valid expiration."
                );
                errors = true;
            }
            this.payment.expiryStr = `20${this.payment.expiryYear}-${this.payment.expiryMonth}`;
            if (
                !errors &&
                (isNaN(parseInt(this.payment.cvv)) ||
                    this.payment.cvv.length > 4 ||
                    this.payment.cvv.length < 3)
            ) {
                this.$set(this.payment.errors, "cvv", "Example: 927");
                errors = true;
            }
            if (!errors) {
                Object.assign(this.payment, {
                    subtotal: this.subtotal,
                    discount: this.discount,
                    shippingAndHandling: this.shippingAndHandling,
                    tax: this.tax,
                    totalFull: this.totalFull
                });
                this.reviewing = true;
            }

            this.disableForms = false;
        },

        // address methods
        createAddress() {
            return {
                firstName: null,
                lastName: null,
                addressLine1: null,
                addressLine2: null,
                country: null,
                city: null,
                state: null,
                zipCode: null,
                zipPlusFour: null,
                errors: null
            };
        },
        getAddressFields() {
            return [
                {
                    id: "firstName",
                    label: "First Name",
                    type: "text",
                    colSpan: 3
                },
                {
                    id: "lastName",
                    label: "Last Name",
                    type: "text",
                    colSpan: 3
                },
                {
                    id: "addressLine1",
                    label: "Address Line 1",
                    type: "text",
                    colSpan: 6
                },
                {
                    id: "addressLine2",
                    label: "Address Line 2",
                    type: "text",
                    colSpan: 6
                },
                { id: "city", label: "City", type: "text", colSpan: 2 },
                {
                    id: "state",
                    label: "State / Province",
                    type: "text",
                    colSpan: 2
                },
                {
                    id: "zipCode",
                    label: "Zip / Postal Code",
                    type: "text",
                    colSpan: 2,
                    maxLength: 10
                },
                {
                    id: "country",
                    label: "Country / Region",
                    type: "select",
                    options: ["United States", "Canada"],
                    colSpan: 3
                }
            ];
        },
        async verifyAddress(address, type) {
            const suggested = `suggested${type.charAt(0).toUpperCase() +
                type.slice(1)}`;
            const found = `found${type.charAt(0).toUpperCase() +
                type.slice(1)}Address`;

            this[found] = false;
            this[suggested].errors = null;

            try {
                await this.addressApiService
                    .verifyAddress(
                        address.addressLine1,
                        address.addressLine2,
                        address.city,
                        address.state,
                        address.zipCode,
                        address.country
                    )
                    .then(response => {
                        if (response.status === 200 && response.data) {
                            if (
                                response.data.dpvconfirmation === "false" &&
                                response.data.errors
                            ) {
                                this[suggested].errors = response.data.errors;
                            } else {
                                this[`${type}AddressSelected`] = true;
                                this[suggested].firstName = this[
                                    type
                                ].firstName;
                                this[suggested].lastName = this[type].lastName;
                                this[suggested].addressLine1 = response.data.address1;
                                this[suggested].addressLine2 = response.data.address2;
                                this[suggested].city = response.data.city;
                                this[suggested].state = response.data.state;
                                this[suggested].zipCode = response.data.zip5;
                                this[suggested].zipPlusFour = response.data.zip4;
                                this[suggested].country = this[type].country;
                            }

                            this[found] = true;
                        }
                    });
            } catch (error) {
                console.error(
                    `An error occurred while verifying the address. ERR:`,
                    error
                );
            }
        },
        selectShippingAddress(selected) {
            this.useSuggestedShipping = selected !== "provided";
        },
        selectBillingAddress(selected) {
            this.useSuggestedBilling = selected !== "provided";
        },
        async CheckAddress() {
            this.disableForms = true;

            const fieldNames = {
                firstName: "First name",
                lastName: "Last name",
                addressLine1: "Address Line 1",
                city: "City",
                state: "State / Province",
                country: "Country / Region",
                zipCode: "Zip / Postal Code"
            };

            let errors = false;

            const setErrors = (model, fields) => {
                this.$set(model, "errors", {});
                fields.forEach(f => {
                    if (!model[f]) {
                        this.$set(
                            model.errors,
                            f,
                            `${fieldNames[f]} must be provided.`
                        );
                        errors = true;
                    }
                });
            };

            setErrors(this.shipping, this.requiredFields.address);
            if (this.isBillingDifferent) {
                setErrors(this.billing, this.requiredFields.address);
            }

            if (!errors && !this.validateZip(this.shipping.zipCode)) {
                this.$set(this.shipping.errors, "zipCode", "Invalid zip code.");
                errors = true;
            }

            if (
                !errors &&
                this.isBillingDifferent &&
                !this.validateZip(this.billing.zipCode)
            ) {
                this.$set(this.billing.errors, "zipCode", "Invalid zip code.");
                errors = true;
            }

            if (!errors) {
                if (this.shipping.country === "United States") {
                    await this.verifyAddress(this.shipping, "shipping");
                }

                if (
                    this.billing.country === "United States" &&
                    this.isBillingDifferent
                ) {
                    await this.verifyAddress(this.billing, "billing");
                }

                if (
                    this.suggestedShipping.errors ||
                    (this.isBillingDifferent && this.suggestedBilling.errors)
                ) {
                    console.log(this.suggestedShipping.errors);

                    if (this.isBillingDifferent) {
                        console.log(this.suggestedBilling.errors);
                    }
                } else {
                    $("#address-selection").modal("show");
                }
            }

            this.disableForms = false;
        },
        applySelectedAddresses() {
            if (this.useSuggestedShipping) {
                this.shipping = { ...this.suggestedShipping };
            }

            if (this.isBillingDifferent && this.useSuggestedBilling) {
                this.billing = { ...this.suggestedBilling };
            } else if (!this.isBillingDifferent) {
                this.billing = { ...this.shipping };
            }

            this.addressVerified = true;

            $("#address-selection").modal("hide");
        },

        // error handling
        resetErrors(model) {
            model.errors = {};
        },
        removeErrors(a, field) {
            if (a.errors && a.errors[field]) a.errors[field] = null;
        },
        validateZip(s) {
            return /^\d{5}(-\d{4})?$/.test(s);
        },
        isFutureDate(d) {
            return d >= new Date(Date.now());
        }
    }
};
</script>

<style lang="scss" scoped>
.errors {
    font-size: 0.75em;
    color: #e64847;
}

.not-valid {
    border-color: #e64847;
}

#reviewTable tr > td:nth-child(2) {
    text-align: right;
}

.form-input-wrapper:focus-within {
    outline: none;
    box-shadow: 0 0 0 3px rgba(164, 202, 254, 0.45);
    border-color: #a4cafe;
}

.form-input-wrapper input:focus {
    outline: none;
}

#card {
    display: inline-block;
}

.address-select {
    background-color: #eff7f7;
    border-width: 2px;
    border-style: solid;
    border-color: transparent;
    border-radius: 0.375rem;
    padding: 1rem;
    cursor: pointer;

    h4 {
        color: #057a55;
        margin-bottom: 0.5rem;
    }

    address {
        font-style: normal;

        p {
            font-size: 0.875rem;
        }
    }

    &.selected {
        border-color: #057a55;
    }
}
</style>
